// RAL Classic colour table (216 colours): RAL number, English name and
// the matching sRGB hex. Used by ColorService.nearestRal to report the closest
// RAL Classic match for any colour (like the nearest web-safe value).
//
// SOURCE: open data only. RAL Classic hex/sRGB values + English names from the
// community CSV at https://gist.github.com/lunohodov/1995178 (public-domain
// colour facts; RAL numbers/names are RAL trademarks used here for reference).
// The match is always approximate: RAL colours are physical paint samples, so a
// screen sRGB value rarely lands exactly on one.

export interface RalColor {
  code: string;   // RAL Classic number, e.g. '5012'
  name: string;   // English name, e.g. 'Light blue'
  hex: string;    // matching sRGB hex, lower-case
}

export const RAL_CLASSIC: RalColor[] = [
  { code: '1000', name: 'Green beige', hex: '#cdba88' },
  { code: '1001', name: 'Beige', hex: '#d0b084' },
  { code: '1002', name: 'Sand yellow', hex: '#d2aa6d' },
  { code: '1003', name: 'Signal yellow', hex: '#f9a900' },
  { code: '1004', name: 'Golden yellow', hex: '#e49e00' },
  { code: '1005', name: 'Honey yellow', hex: '#cb8f00' },
  { code: '1006', name: 'Maize yellow', hex: '#e19000' },
  { code: '1007', name: 'Daffodil yellow', hex: '#e88c00' },
  { code: '1011', name: 'Brown beige', hex: '#af8050' },
  { code: '1012', name: 'Lemon yellow', hex: '#ddaf28' },
  { code: '1013', name: 'Oyster white', hex: '#e3d9c7' },
  { code: '1014', name: 'Ivory', hex: '#ddc49b' },
  { code: '1015', name: 'Light ivory', hex: '#e6d2b5' },
  { code: '1016', name: 'Sulfur yellow', hex: '#f1dd39' },
  { code: '1017', name: 'Saffron yellow', hex: '#f6a951' },
  { code: '1018', name: 'Zinc yellow', hex: '#faca31' },
  { code: '1019', name: 'Grey beige', hex: '#a48f7a' },
  { code: '1020', name: 'Olive yellow', hex: '#a08f65' },
  { code: '1021', name: 'Colza yellow', hex: '#f6b600' },
  { code: '1023', name: 'Traffic yellow', hex: '#f7b500' },
  { code: '1024', name: 'Ochre yellow', hex: '#ba8f4c' },
  { code: '1026', name: 'Luminous yellow', hex: '#ffff00' },
  { code: '1027', name: 'Curry', hex: '#a77f0f' },
  { code: '1028', name: 'Melon yellow', hex: '#ff9c00' },
  { code: '1032', name: 'Broom yellow', hex: '#e2a300' },
  { code: '1033', name: 'Dahlia yellow', hex: '#f99a1d' },
  { code: '1034', name: 'Pastel yellow', hex: '#eb9c52' },
  { code: '1035', name: 'Pearl beige', hex: '#8f8370' },
  { code: '1036', name: 'Pearl gold', hex: '#806440' },
  { code: '1037', name: 'Sun yellow', hex: '#f09200' },
  { code: '2000', name: 'Yellow orange', hex: '#da6e00' },
  { code: '2001', name: 'Red orange', hex: '#ba481c' },
  { code: '2002', name: 'Vermilion', hex: '#bf3922' },
  { code: '2003', name: 'Pastel orange', hex: '#f67829' },
  { code: '2004', name: 'Pure orange', hex: '#e25304' },
  { code: '2005', name: 'Luminous orange', hex: '#ff4d08' },
  { code: '2007', name: 'Luminous bright orange', hex: '#ffb200' },
  { code: '2008', name: 'Bright red orange', hex: '#ec6b22' },
  { code: '2009', name: 'Traffic orange', hex: '#de5308' },
  { code: '2010', name: 'Signal orange', hex: '#d05d29' },
  { code: '2011', name: 'Deep orange', hex: '#e26e0f' },
  { code: '2012', name: 'Salmon orange', hex: '#d5654e' },
  { code: '2013', name: 'Pearl orange', hex: '#923e25' },
  { code: '2017', name: 'RAL orange', hex: '#fc5500' },
  { code: '3000', name: 'Flame red', hex: '#a72920' },
  { code: '3001', name: 'Signal red', hex: '#9b2423' },
  { code: '3002', name: 'Carmine red', hex: '#9b2321' },
  { code: '3003', name: 'Ruby red', hex: '#861a22' },
  { code: '3004', name: 'Purple red', hex: '#6b1c23' },
  { code: '3005', name: 'Wine red', hex: '#59191f' },
  { code: '3007', name: 'Black red', hex: '#3e2022' },
  { code: '3009', name: 'Oxide red', hex: '#6d342d' },
  { code: '3011', name: 'Brown red', hex: '#782423' },
  { code: '3012', name: 'Beige red', hex: '#c5856d' },
  { code: '3013', name: 'Tomato red', hex: '#972e25' },
  { code: '3014', name: 'Antique pink', hex: '#cb7375' },
  { code: '3015', name: 'Light pink', hex: '#d8a0a6' },
  { code: '3016', name: 'Coral red', hex: '#a63d30' },
  { code: '3017', name: 'Rose', hex: '#ca555d' },
  { code: '3018', name: 'Strawberry red', hex: '#c63f4a' },
  { code: '3020', name: 'Traffic red', hex: '#bb1f11' },
  { code: '3022', name: 'Salmon pink', hex: '#cf6955' },
  { code: '3024', name: 'Luminous red', hex: '#ff2d21' },
  { code: '3026', name: 'Luminous bright red', hex: '#ff2a1c' },
  { code: '3027', name: 'Raspberry red', hex: '#ab273c' },
  { code: '3028', name: 'Pure red', hex: '#cc2c24' },
  { code: '3031', name: 'Orient red', hex: '#a63437' },
  { code: '3032', name: 'Pearl ruby red', hex: '#701d24' },
  { code: '3033', name: 'Pearl pink', hex: '#a53a2e' },
  { code: '4001', name: 'Red lilac', hex: '#816183' },
  { code: '4002', name: 'Red violet', hex: '#8d3c4b' },
  { code: '4003', name: 'Heather violet', hex: '#c4618c' },
  { code: '4004', name: 'Claret violet', hex: '#651e38' },
  { code: '4005', name: 'Blue lilac', hex: '#76689a' },
  { code: '4006', name: 'Traffic purple', hex: '#903373' },
  { code: '4007', name: 'Purple violet', hex: '#47243c' },
  { code: '4008', name: 'Signal violet', hex: '#844c82' },
  { code: '4009', name: 'Pastel violet', hex: '#9d8692' },
  { code: '4010', name: 'Telemagenta', hex: '#bb4077' },
  { code: '4011', name: 'Pearl violet', hex: '#6e6387' },
  { code: '4012', name: 'Pearl blackberry', hex: '#6a6b7f' },
  { code: '5000', name: 'Violet blue', hex: '#304f6e' },
  { code: '5001', name: 'Green blue', hex: '#0e4c64' },
  { code: '5002', name: 'Ultramarine blue', hex: '#00387a' },
  { code: '5003', name: 'Sapphire blue', hex: '#1f3855' },
  { code: '5004', name: 'Black blue', hex: '#191e28' },
  { code: '5005', name: 'Signal blue', hex: '#005387' },
  { code: '5007', name: 'Brillant blue', hex: '#376b8c' },
  { code: '5008', name: 'Grey blue', hex: '#2b3a44' },
  { code: '5009', name: 'Azure blue', hex: '#215f78' },
  { code: '5010', name: 'Gentian blue', hex: '#004f7c' },
  { code: '5011', name: 'Steel blue', hex: '#1a2b3c' },
  { code: '5012', name: 'Light blue', hex: '#0089b6' },
  { code: '5013', name: 'Cobalt blue', hex: '#193153' },
  { code: '5014', name: 'Pigeon blue', hex: '#637d96' },
  { code: '5015', name: 'Sky blue', hex: '#007caf' },
  { code: '5017', name: 'Traffic blue', hex: '#005b8c' },
  { code: '5018', name: 'Turquoise blue', hex: '#048b8c' },
  { code: '5019', name: 'Capri blue', hex: '#005e83' },
  { code: '5020', name: 'Ocean blue', hex: '#00414b' },
  { code: '5021', name: 'Water blue', hex: '#007577' },
  { code: '5022', name: 'Night blue', hex: '#222d5a' },
  { code: '5023', name: 'Distant blue', hex: '#41698c' },
  { code: '5024', name: 'Pastel blue', hex: '#6093ac' },
  { code: '5025', name: 'Pearl gentian blue', hex: '#20697c' },
  { code: '5026', name: 'Pearl night blue', hex: '#0f3052' },
  { code: '6000', name: 'Patina green', hex: '#3c7460' },
  { code: '6001', name: 'Emerald green', hex: '#366735' },
  { code: '6002', name: 'Leaf green', hex: '#325928' },
  { code: '6003', name: 'Olive green', hex: '#50533c' },
  { code: '6004', name: 'Blue green', hex: '#024442' },
  { code: '6005', name: 'Moss green', hex: '#114232' },
  { code: '6006', name: 'Grey olive', hex: '#3c392e' },
  { code: '6007', name: 'Bottle green', hex: '#2c3222' },
  { code: '6008', name: 'Brown green', hex: '#36342a' },
  { code: '6009', name: 'Fir green', hex: '#27352a' },
  { code: '6010', name: 'Grass green', hex: '#4d6f39' },
  { code: '6011', name: 'Reseda green', hex: '#6b7c59' },
  { code: '6012', name: 'Black green', hex: '#2f3d3a' },
  { code: '6013', name: 'Reed green', hex: '#7c765a' },
  { code: '6014', name: 'Yellow olive', hex: '#474135' },
  { code: '6015', name: 'Black olive', hex: '#3d3d36' },
  { code: '6016', name: 'Turquoise green', hex: '#00694c' },
  { code: '6017', name: 'May green', hex: '#587f40' },
  { code: '6018', name: 'Yellow green', hex: '#60993b' },
  { code: '6019', name: 'Pastel green', hex: '#b9ceac' },
  { code: '6020', name: 'Chrome green', hex: '#37422f' },
  { code: '6021', name: 'Pale green', hex: '#8a9977' },
  { code: '6022', name: 'Olive drab', hex: '#3a3327' },
  { code: '6024', name: 'Traffic green', hex: '#008351' },
  { code: '6025', name: 'Fern green', hex: '#5e6e3b' },
  { code: '6026', name: 'Opal green', hex: '#005f4e' },
  { code: '6027', name: 'Light green', hex: '#7ebab5' },
  { code: '6028', name: 'Pine green', hex: '#315442' },
  { code: '6029', name: 'Mint green', hex: '#006f3d' },
  { code: '6032', name: 'Signal green', hex: '#237f52' },
  { code: '6033', name: 'Mint turquoise', hex: '#45877f' },
  { code: '6034', name: 'Pastel turquoise', hex: '#7aadac' },
  { code: '6035', name: 'Pearl green', hex: '#194d25' },
  { code: '6036', name: 'Pearl opal green', hex: '#04574b' },
  { code: '6037', name: 'Pure green', hex: '#008b29' },
  { code: '6038', name: 'Luminous green', hex: '#00b51b' },
  { code: '6039', name: 'Fibrous green', hex: '#b3c43e' },
  { code: '7000', name: 'Squirrel grey', hex: '#7a888e' },
  { code: '7001', name: 'Silver grey', hex: '#8c979c' },
  { code: '7002', name: 'Olive grey', hex: '#817863' },
  { code: '7003', name: 'Moss grey', hex: '#797669' },
  { code: '7004', name: 'Signal grey', hex: '#9a9b9b' },
  { code: '7005', name: 'Mouse grey', hex: '#6b6e6b' },
  { code: '7006', name: 'Beige grey', hex: '#766a5e' },
  { code: '7008', name: 'Khaki grey', hex: '#745f3d' },
  { code: '7009', name: 'Green grey', hex: '#5d6058' },
  { code: '7010', name: 'Tarpaulin grey', hex: '#585c56' },
  { code: '7011', name: 'Iron grey', hex: '#52595d' },
  { code: '7012', name: 'Basalt grey', hex: '#575d5e' },
  { code: '7013', name: 'Brown grey', hex: '#575044' },
  { code: '7015', name: 'Slate grey', hex: '#4f5358' },
  { code: '7016', name: 'Anthracite grey', hex: '#383e42' },
  { code: '7021', name: 'Black grey', hex: '#2f3234' },
  { code: '7022', name: 'Umbra grey', hex: '#4c4a44' },
  { code: '7023', name: 'Concrete grey', hex: '#808076' },
  { code: '7024', name: 'Graphite grey', hex: '#45494e' },
  { code: '7026', name: 'Granite grey', hex: '#374345' },
  { code: '7030', name: 'Stone grey', hex: '#928e85' },
  { code: '7031', name: 'Blue grey', hex: '#5b686d' },
  { code: '7032', name: 'Pebble grey', hex: '#b5b0a1' },
  { code: '7033', name: 'Cement grey', hex: '#7f8274' },
  { code: '7034', name: 'Yellow grey', hex: '#92886f' },
  { code: '7035', name: 'Light grey', hex: '#c5c7c4' },
  { code: '7036', name: 'Platinum grey', hex: '#979392' },
  { code: '7037', name: 'Dusty grey', hex: '#7a7b7a' },
  { code: '7038', name: 'Agate grey', hex: '#b0b0a9' },
  { code: '7039', name: 'Quartz grey', hex: '#6b665e' },
  { code: '7040', name: 'Window grey', hex: '#989ea1' },
  { code: '7042', name: 'Traffic grey A', hex: '#8e9291' },
  { code: '7043', name: 'Traffic grey B', hex: '#4f5250' },
  { code: '7044', name: 'Silk grey', hex: '#b7b3a8' },
  { code: '7045', name: 'Telegrey 1', hex: '#8d9295' },
  { code: '7046', name: 'Telegrey 2', hex: '#7e868a' },
  { code: '7047', name: 'Telegrey 4', hex: '#c8c8c7' },
  { code: '7048', name: 'Pearl mouse grey', hex: '#817b73' },
  { code: '8000', name: 'Green brown', hex: '#89693f' },
  { code: '8001', name: 'Ochre brown', hex: '#9d622b' },
  { code: '8002', name: 'Signal brown', hex: '#794d3e' },
  { code: '8003', name: 'Clay brown', hex: '#7e4b27' },
  { code: '8004', name: 'Copper brown', hex: '#8d4931' },
  { code: '8007', name: 'Fawn brown', hex: '#70462b' },
  { code: '8008', name: 'Olive brown', hex: '#724a25' },
  { code: '8011', name: 'Nut brown', hex: '#5a3827' },
  { code: '8012', name: 'Red brown', hex: '#66332b' },
  { code: '8014', name: 'Sepia brown', hex: '#4a3526' },
  { code: '8015', name: 'Chestnut brown', hex: '#5e2f26' },
  { code: '8016', name: 'Mahogany brown', hex: '#4c2b20' },
  { code: '8017', name: 'Chocolate brown', hex: '#442f29' },
  { code: '8019', name: 'Grey brown', hex: '#3d3635' },
  { code: '8022', name: 'Black brown', hex: '#1a1719' },
  { code: '8023', name: 'Orange brown', hex: '#a45729' },
  { code: '8024', name: 'Beige brown', hex: '#795038' },
  { code: '8025', name: 'Pale brown', hex: '#755847' },
  { code: '8028', name: 'Terra brown', hex: '#513a2a' },
  { code: '8029', name: 'Pearl copper', hex: '#7f4031' },
  { code: '9001', name: 'Cream', hex: '#e9e0d2' },
  { code: '9002', name: 'Grey white', hex: '#d6d5cb' },
  { code: '9003', name: 'Signal white', hex: '#ecece7' },
  { code: '9004', name: 'Signal black', hex: '#2b2b2c' },
  { code: '9005', name: 'Jet black', hex: '#0e0e10' },
  { code: '9006', name: 'White aluminium', hex: '#a1a1a0' },
  { code: '9007', name: 'Grey aluminium', hex: '#868581' },
  { code: '9010', name: 'Pure white', hex: '#f1ede1' },
  { code: '9011', name: 'Graphite black', hex: '#27292b' },
  { code: '9012', name: 'Cleanroom white', hex: '#f8f2e1' },
  { code: '9016', name: 'Traffic white', hex: '#f1f1ea' },
  { code: '9017', name: 'Traffic black', hex: '#29292a' },
  { code: '9018', name: 'Papyrus white', hex: '#c8cbc4' },
  { code: '9022', name: 'Pearl light grey', hex: '#858583' },
  { code: '9023', name: 'Pearl dark grey', hex: '#787b7a' },
];
